import { createContext, useContext, useState, type ReactNode } from "react";
import { Activity, Search, CalendarClock, X } from "lucide-react";
import { Button, Field, SummaryMetrics } from "../../design-system";
import { collectedAt } from "./seo-data";
import "./seo.css";

export const SeoStaleContext = createContext(false);
const displayedDate = (date: string, stale: boolean) =>
  stale && date > "2026.10.12" ? "2026.10.12" : date;
export function SeoMeta({
  source = "네이버 검색 API",
  finalDate = "2026.10.15",
  collected = collectedAt,
}: {
  source?: string;
  finalDate?: string;
  collected?: string;
}) {
  const stale = useContext(SeoStaleContext);
  return (
    <p className="seo-meta">
      {source} · 마지막 수집 {stale ? "2026.10.12 14:00" : collected} · 최종
      데이터 {displayedDate(finalDate, stale)}
    </p>
  );
}
export function SeoNotice({ children }: { children: ReactNode }) {
  return (
    <div className="seo-notice">
      <Search size={16} />
      <span>{children}</span>
    </div>
  );
}
export function SeoMetrics({
  items,
  source,
  finalDate,
  collected = collectedAt,
}: {
  collected?: string;
  items: {
    label: string;
    value: string | number;
    change: string;
    finalDate?: string;
    source?: string;
  }[];
  source?: string;
  finalDate?: string;
}) {
  const stale = useContext(SeoStaleContext);
  return (
    <SummaryMetrics
      columns={items.length === 6 ? 3 : undefined}
      items={items.map((i) => ({
        label: i.label,
        value: i.value,
        icon: <Activity />,
        note: `${i.change} · ${i.source ?? source ?? "검색 API"} · 수집 ${stale ? "2026.10.12 14:00" : collected} / 데이터 ${displayedDate(i.finalDate ?? finalDate ?? "2026.10.15", stale)}`,
      }))}
    />
  );
}
export function SeoEmpty({ pending = false }: { pending?: boolean }) {
  return (
    <div className="seo-empty">
      <CalendarClock size={32} />
      <strong>
        {pending
          ? "첫 번째 검색 결과를 기다리고 있습니다"
          : "아직 분석 중인 키워드가 없습니다"}
      </strong>
      <span>
        {pending
          ? "첫 수집 예정 15:00 · 작업 요청 시점부터 추적을 시작합니다."
          : "발행 작업에 등록된 키워드의 분석 결과가 이곳에 표시됩니다."}
      </span>
    </div>
  );
}
export function KeywordInput({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const add = () => {
    const next = [
      ...new Set([
        ...value,
        ...text
          .split(/[,\n]/)
          .map((v) =>
            v.normalize("NFKC").trim().replace(/\s+/g, " ").toLowerCase(),
          )
          .filter(Boolean),
      ]),
    ];
    if (next.length > 20) {
      setError("키워드는 최대 20개까지 등록할 수 있습니다.");
      return;
    }
    onChange(next);
    setText("");
    setError("");
  };
  return (
    <div className="seo-keyword-input">
      <div className="seo-keyword-entry">
        <Field
          label={`SEO 키워드 · ${value.length}/20`}
          placeholder="예: 강남 내과, 건강검진 예약"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.nativeEvent.isComposing) {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button type="button" variant="secondary" onClick={add}>
          추가
        </Button>
      </div>
      <div className="seo-chips">
        {value.map((v) => (
          <span key={v}>
            {v}
            <button
              type="button"
              aria-label={`${v} 삭제`}
              onClick={() => onChange(value.filter((k) => k !== v))}
            >
              <X size={14} />
            </button>
          </span>
        ))}
      </div>
      <p className="seo-caption">
        1–20개 · Enter 또는 추가로 등록 · 중복 키워드는 합칩니다. 요청 즉시
        추적하며, 마지막 요청부터 90일간 유지합니다.
      </p>
      {error && (
        <p role="alert" className="seo-error">
          {error}
        </p>
      )}
    </div>
  );
}
